import { PMS_API_URLS } from "../_constants/PMS_API_URLS";
import { ServiceHelper } from "../../Everou/_services/ServiceHelper";
import { PMSLocationsPaginationQueryParams } from "../_models/Pagination";
import { URL_CONSTANTS } from "../../Everou/_constants";
import { PMSIntegrationCreateServer, PMSIntegrationUpdateServer } from "PMS/_models/PMSIntegration";

export const PMSLocationsService = {
    list,
    getLogs,
    getProtectLogs,
    getTriggeredProtectLogs,
    locationInfo,
    reservations,
    createNewLocationTag,
    deleteLocationTag,
    integrations,
    groups,
    createLocationIntegration,
    updateLocationIntegration,
    moveLocationIntegrationTemplate,
    deleteLocationIntegration,
    updateLocationURLRecommendations,
    updateLocationTravelerDiscount,
    updateTimezone,
    updateCoordinates,
    updateInvitationLinkAddToHome,
    updateInvitationLinkAppName,
    updateLocationField,
    changeProtectStatus,
    changeAutoProtectSetting,
    updateURLVideo,
    createReservation,
    deleteReservation,
    updateReservation,
    updatePublicDeviceOverrides,
    listEmbedDomains,
    createEmbedDomain,
    updateEmbedDomain,
    deleteEmbedDomain,
};

export interface PMSEmbedDomain {
    id: number;
    domain: string;
    is_active: boolean;
    note: string;
    created_on: string;
}

export interface PMSEmbedDomainPatch {
    domain?: string;
    is_active?: boolean;
    note?: string;
}

async function list(queryParams: PMSLocationsPaginationQueryParams & {offset?: number}) {
    return ServiceHelper.getRequest(PMS_API_URLS.LOCATIONS, queryParams);
}

async function getLogs(
    locationUid,
    startDate,
    endDate,
    pageSize,
    page
) {
    const params = `\
        ?loc_uid=${locationUid}\
        &begin=${startDate}\
        &end=${endDate}\
        &page_size=${pageSize}\
        &offset=${page}\
    `;
    const paramsNoSpaces = params.replace(/\s/g, '');
    const response = await ServiceHelper.getRequest(URL_CONSTANTS.LOCATION_LOG, paramsNoSpaces);

    return {
        logs: response.logs,
        next: response.next,
    };
}

// Currently-triggered protect events (grouped). Non-empty means the alarm has
// gone off — same call the Everou Protect module uses for its triggered logs.
async function getTriggeredProtectLogs(locationUid) {
    const response = await ServiceHelper.getRequest(
        URL_CONSTANTS.LOCATION_LOG,
        `?loc_uid=${locationUid}&offset=0&group=1&protect=true`,
    );
    return response.logs;
}

// Protect activity. Same shape as getLogs but hits the dedicated protect log
// endpoint so it only returns alarm (protect) events.
async function getProtectLogs(
    locationUid,
    startDate,
    endDate,
    pageSize,
    page
) {
    const params = `\
        ?loc_uid=${locationUid}\
        &begin=${startDate}\
        &end=${endDate}\
        &page_size=${pageSize}\
        &offset=${page}\
    `;
    const paramsNoSpaces = params.replace(/\s/g, '');
    const response = await ServiceHelper.getRequest(URL_CONSTANTS.LOCATION_PROTECT_LOG, paramsNoSpaces);

    return {
        logs: response.logs,
        next: response.next,
    };
}

async function locationInfo(uid) {
    const parameters = "?loc_uid=" + uid;
    const response = await ServiceHelper.getRequest(URL_CONSTANTS.LOCATION_INFO, parameters);
    return response;
}

export interface PMSReservationFilters {
    status?: string;
    date_from?: string;
    date_to?: string;
    reservation_id?: string;
}

async function reservations(uid: string, filters: PMSReservationFilters = {}) {
    // getRequest serializes the object and drops null/undefined/'' params, so
    // unset filters are simply omitted from the query string.
    return ServiceHelper.getRequest(PMS_API_URLS.RESERVATIONS, { uid, ...filters });
}

async function createNewLocationTag({tag, locationUid}) {
    return ServiceHelper.postRequest(
        PMS_API_URLS.TAGS,
        {
            loc_uid: locationUid,
            tag: tag,
        },
    );
}

async function deleteLocationTag({tag, locationUid}) {
    return ServiceHelper.deleteRequest(
        PMS_API_URLS.TAGS,
        {
            tag: tag,
            loc_uid: locationUid
        },
    );
}

async function integrations(uid) {
    const parameters = "?loc_uid=" + uid;
    return ServiceHelper.getRequest(PMS_API_URLS.LOCATION_INTEGRATIONS, parameters);
}

async function groups(uid) {
    const parameters = "?loc_uid=" + uid;
    return ServiceHelper.getRequest(PMS_API_URLS.GROUPS, parameters);
}

async function createLocationIntegration(serverIntegration: PMSIntegrationCreateServer) {
    return ServiceHelper.putRequest(PMS_API_URLS.LOCATION_INTEGRATIONS, { ...serverIntegration });
}

// Partial update of an existing location integration. The backend's PUT branches
// on `integration_uid`: when present it updates that integration instead of
// creating a new one from a template uid. Only the fields sent are overwritten.
async function updateLocationIntegration(update: PMSIntegrationUpdateServer) {
    return ServiceHelper.putRequest(PMS_API_URLS.LOCATION_INTEGRATIONS, { ...update });
}

// PATCH, not PUT: a PUT without `integration_uid` creates, so the explicit verb
// is what makes "edit this one" unambiguous. Used for the template move, which
// re-credentials the integration server-side.
async function moveLocationIntegrationTemplate(update: PMSIntegrationUpdateServer) {
    return ServiceHelper.patchRequest(PMS_API_URLS.LOCATION_INTEGRATIONS, { ...update });
}

// `deleteInvitations` defaults to true on the backend: when omitted the
// integration's group, guest invitations, keypad codes and links are all
// removed. Pass false to keep guest access (e.g. when migrating between PMSs).
async function deleteLocationIntegration(uid: string, deleteInvitations: boolean = true) {
    return ServiceHelper.deleteRequest(PMS_API_URLS.LOCATION_INTEGRATIONS, {
        uid,
        delete_invitations: deleteInvitations,
    });
}

async function updateLocationURLRecommendations({ uid, url_recommendations }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            url_recommendations,
        },
    );
}

async function updateLocationTravelerDiscount({ uid, traveler_discount }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            traveler_discount,
        },
    );
}

async function updateTimezone({ uid, timezone }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            timezone,
        },
    );
}

// The address travels with the coordinates and is always sent, empty included:
// a point picked straight off the map has no address, and leaving the previous
// one would label the new point with the old place.
async function updateCoordinates({ uid, latitude, longitude, address }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            lat: latitude,
            long: longitude,
            address,
        },
    );
}

async function updateInvitationLinkAddToHome({ uid, invitation_link_add_to_home }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            invitation_link_add_to_home,
        },
    );
}

async function updateInvitationLinkAppName({ uid, invitation_link_app_name }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            invitation_link_app_name,
        },
    );
}

// Generic single- or multi-field location update. Every location feature
// toggle POSTs `{ loc_uid, ...fields }` to the same LOCATION_UPDATE endpoint,
// so they share this instead of one near-identical function each.
// `security_alert_time` is the door-open alert threshold in seconds (0 = off,
// max 32767, validated by the backend).
async function updateLocationField({ uid, ...fields }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            ...fields,
        },
    );
}

async function changeProtectStatus(enabled, locationUid) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: locationUid,
            protect: enabled,
        },
    );
}

async function changeAutoProtectSetting(enabled, locationUid) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: locationUid,
            protect_auto_enabled_without_reservations: enabled,
        },
    );
}

async function updateURLVideo({ uid, external_video_url }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            external_video_url,
        },
    );
}

function createReservation(
    integration_uid: string,
    group_id: number,
    begin: string,
    end: string,
    email: string = "",
    name: string = "",
    send_email: boolean = false
) {
    return ServiceHelper.putRequest(
        PMS_API_URLS.RESERVATIONS,
        {
            integration_uid,
            group_id,
            begin,
            end,
            email,
            name,
            send_email
        }
    );
}

function deleteReservation(
    reservation_uid: string
) {
    return ServiceHelper.deleteRequest(
        PMS_API_URLS.RESERVATIONS,
        {
            reservation_uid
        }
    );
}

function updateReservation(
    reservation_uid: string,
    begin: string,
    end: string,
) {
    return ServiceHelper.postRequest(
        PMS_API_URLS.RESERVATIONS,
        {
            reservation_uid,
            begin,
            end
        }
    );
}

// Pending backend support for the `public_device_overrides` field on LOCATION_UPDATE.
async function updatePublicDeviceOverrides({ uid, overrides }) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.LOCATION_UPDATE,
        {
            loc_uid: uid,
            public_device_overrides: overrides.map(o => ({
                device_uid: o.deviceUid,
                mode: o.mode,
            })),
        },
    );
}

// Domains allowed to embed this location's public link in an iframe. The
// backend keys everything off loc_uid (admin-only) and caps the list at 50.
async function listEmbedDomains(locationUid: string): Promise<PMSEmbedDomain[]> {
    return ServiceHelper.getRequest(PMS_API_URLS.LOCATION_EMBED_DOMAINS, { loc_uid: locationUid });
}

async function createEmbedDomain(locationUid: string, domain: string, note = ""): Promise<PMSEmbedDomain> {
    return ServiceHelper.postRequest(PMS_API_URLS.LOCATION_EMBED_DOMAINS, {
        loc_uid: locationUid,
        domain,
        note,
    });
}

async function updateEmbedDomain(locationUid: string, id: number, patch: PMSEmbedDomainPatch): Promise<PMSEmbedDomain> {
    return ServiceHelper.putRequest(PMS_API_URLS.LOCATION_EMBED_DOMAINS, {
        loc_uid: locationUid,
        id,
        ...patch,
    });
}

async function deleteEmbedDomain(locationUid: string, id: number) {
    return ServiceHelper.deleteRequest(PMS_API_URLS.LOCATION_EMBED_DOMAINS, {
        loc_uid: locationUid,
        id,
    });
}